import React, { FormEvent, ReactElement, useCallback, useRef, useState } from 'react';
import { ApolloError } from '@apollo/client';
import Autosuggest from 'react-autosuggest';
import classNames from 'classnames';

// NOTE set the width of the input field on the parent component/div
// Pass in two functions, <changeIntercept> and <selectIntercept> that will run
// when input is changed or when an item from the autocomplete menu is selected

// https://github.com/moroshko/react-autosuggest

import LoadingSpinner from '@buyside/components/presentation/LoadingSpinner/loadingSpinner';
import { Suggestion as AutoCompleteFieldSuggestion } from '@buyside/components/presentation/Form/Field/AutocompleteField/autocompleteField';
import { AutocompleteListItem as EntityMentionAutocompleteSuggestion } from '@buyside/components/presentation/EntityMention/entityMentionAutocomplete';
import { Suggestion as OmniSearchSuggestion } from '@buyside/components/presentation/Omnisearch/omnisearch';

import { debounce as lodashDebounce } from '@buyside/util/lodashAlt';
import { useIsMobile } from '@buyside/util/hooks/useIsMobile';
import { useKeydownRef } from '@buyside/util/hooks/useKeyPress';
import { useHandleFieldArrayRemove } from '@buyside/util/hooks';

import { Entity } from '@interfaces/graphql-typings';

import styles from './autocompleteStyles.scss';

export type Section = {
  showLink: boolean;
  suggestions: AutocompleteSuggestion[];
  title: string;
  url?: string;
};

type AutocompleteSuggestion =
  | AutoCompleteFieldSuggestion
  | EntityMentionAutocompleteSuggestion
  | OmniSearchSuggestion;
export type MentionedEntity = Pick<Entity, 'id' | 'primarySymbol'>;

const Autocomplete: React.FC<{
  analyticsTag?: string;
  autosuggestOptions?: {};
  changeIntercept: (searchTerm: string, reason: string) => void;
  clearInputOnSelect?: boolean;
  debounce?: boolean;
  error?: ApolloError;
  formatSectionTitle?: (section: Section) => ReactElement;
  formatSuggestion?: (suggestion: any) => string;
  forOmnisearch?: boolean;
  getSuggestionValue?: (suggestion: any) => string;
  handleSuggestionsClearRequested?: () => {};
  initialValue?: string;
  inputProps: {};
  isWide?: boolean;
  loading?: boolean;
  selectIntercept: (_mentionDisplay: string, selectedItem: any) => void;
  suggestions: { suggestions: any[] }[] | any[];
}> = ({
  analyticsTag,
  autosuggestOptions,
  changeIntercept,
  clearInputOnSelect,
  debounce,
  error,
  formatSectionTitle,
  formatSuggestion,
  forOmnisearch,
  getSuggestionValue,
  handleSuggestionsClearRequested,
  initialValue,
  inputProps,
  isWide,
  loading,
  selectIntercept,
  suggestions
}) => {
  const isMobile = useIsMobile();

  const [value, setValue] = useState(initialValue || '');

  const handleSuggestionsFetchRequested = ({
    value,
    reason
  }: {
    value: string;
    reason: string;
  }): void => {
    changeIntercept(value, reason);
  };

  const debouncedHandleSuggestionsFetchRequested = useRef(
    debounce
      ? lodashDebounce(handleSuggestionsFetchRequested, 500)
      : handleSuggestionsFetchRequested
  );

  const onKeydown = useCallback(
    (e: KeyboardEvent) => {
      if (e.code === 'Escape' && handleSuggestionsClearRequested) {
        handleSuggestionsClearRequested();
        e.stopPropagation();
      }
    },
    [handleSuggestionsClearRequested]
  );
  const containerRef = useKeydownRef(onKeydown);

  const formattedInputProps = {
    value,
    onChange: (_: FormEvent, { newValue }: { newValue: string }): void => setValue(newValue),
    'data-heap': analyticsTag,
    ...inputProps
  };

  styles.suggestionsContainerOpen = classNames(styles.suggestionsContainerBase, {
    [styles.isWide]: isWide,
    [styles.forOmnisearch]: forOmnisearch && isMobile
  });

  useHandleFieldArrayRemove(initialValue, setValue);

  return (
    <div className={styles.autocompleteContainer} ref={containerRef}>
      <Autosuggest
        suggestions={loading ? [] : suggestions}
        onSuggestionsFetchRequested={debouncedHandleSuggestionsFetchRequested.current}
        onSuggestionSelected={(e, { suggestion, suggestionValue }): void => {
          e.preventDefault();
          selectIntercept(suggestionValue, suggestion);
          setValue(clearInputOnSelect ? '' : suggestionValue);
        }}
        onSuggestionsClearRequested={handleSuggestionsClearRequested || ((): any => {})}
        getSuggestionValue={getSuggestionValue || ((suggestion): string => suggestion?.label || '')}
        renderSuggestion={(suggestion): string =>
          formatSuggestion ? formatSuggestion(suggestion) : suggestion.label
        }
        inputProps={formattedInputProps}
        theme={styles}
        renderSectionTitle={(section: Section): ReactElement =>
          formatSectionTitle ? formatSectionTitle(section) : <strong>{section.title}</strong>
        }
        getSectionSuggestions={(section: Section): any[] => section.suggestions}
        {...autosuggestOptions}
      />
      {loading && (
        <div className={classNames(styles.suggestionsContainerOpen, styles.loadingContainer)}>
          <span className={styles.loadingText}>
            <LoadingSpinner size="nano" /> Loading...
          </span>
        </div>
      )}
      {!loading && error && (
        <div className={classNames(styles.suggestionsContainerOpen, styles.loadingContainer)}>
          <span className={styles.loadingText}>{error.message}</span>
        </div>
      )}
    </div>
  );
};

export default Autocomplete;
